import type { CSSProperties } from 'react';

import { contrastText } from '@/shared/lib/color';

import { gradientCss, gradientText, hexToRgba, isHex } from './css';
import type { SurfaceFill } from './types';

/** The scrim never goes below this over a photo: white text must stay readable on any picture. */
export const MIN_IMAGE_OVERLAY = 0.15;

export interface SurfaceImage {
  url: string;
  /** Focal point, 0..1. */
  x: number;
  y: number;
  /** Alpha of the black scrim over the picture. */
  overlay: number;
}

export interface SurfaceProps {
  /** `true` when the fill replaces the theme surface (then text colors are re-derived). */
  custom: boolean;
  style: CSSProperties | undefined;
  /** An image fill: the picture and scrim are drawn by the surface as layers, not by `style`. */
  image?: SurfaceImage;
}

const clamp = (value: number | undefined, min: number, max: number, fallback: number) =>
  Math.min(max, Math.max(min, Number.isFinite(value) ? (value as number) : fallback));

/**
 * The tokens re-pointed inside a custom surface. Text-ish tokens follow the readable foreground;
 * `--color-surface` becomes a translucent "glass" so controls drawn with the theme surface
 * (secondary buttons, inputs) neither vanish into the fill nor show dark theme text-on-dark.
 * The stored fill is never altered: only the foreground side adapts. The tokens are set on this
 * element only: portaled menus and dialogs keep the theme.
 */
function tokens(fg: '#000000' | '#ffffff', glass: string): CSSProperties {
  const inverse = fg === '#000000' ? '#ffffff' : '#000000';
  return {
    color: fg,
    ['--color-text' as string]: fg,
    ['--color-text-muted' as string]: hexToRgba(fg, 0.82),
    ['--color-surface' as string]: glass,
    ['--color-surface-hover' as string]: hexToRgba(fg, 0.16),
    ['--color-border' as string]: hexToRgba(fg, 0.38),
    ['--color-focus' as string]: fg,
    ['--color-accent' as string]: fg,
    ['--color-accent-hover' as string]: hexToRgba(fg, 0.88),
    ['--color-accent-contrast' as string]: inverse,
  };
}

/**
 * Turns a surface fill into inline CSS (and, for an image, the image layers). The worst point of a
 * gradient decides the foreground, so everything drawn on it stays legible in both themes.
 * `imageUrl` is the picture of an `image` fill (a local object URL while previewing a draft).
 */
export function surfaceProps(
  fill: SurfaceFill | null | undefined,
  imageUrl?: string | null,
): SurfaceProps {
  if (fill?.kind === 'image' && imageUrl) {
    return {
      custom: true,
      image: {
        url: imageUrl,
        x: clamp(fill.position_x, 0, 1, 0.5),
        y: clamp(fill.position_y, 0, 1, 0.5),
        overlay: clamp(fill.overlay, MIN_IMAGE_OVERLAY, 0.8, 0.35),
      },
      style: {
        ...tokens('#ffffff', 'rgba(0, 0, 0, 0.38)'),
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
      },
    };
  }
  let background: string | null = null;
  let fg: '#000000' | '#ffffff' | null = null;
  if (fill?.kind === 'color' && isHex(fill.color)) {
    background = fill.color;
    fg = contrastText(fill.color);
  } else if (fill?.kind === 'gradient' && fill.gradient) {
    background = gradientCss(fill.gradient);
    if (background) fg = gradientText(fill.gradient);
  }
  if (!background || !fg) return { custom: false, style: undefined };
  const glass = fg === '#000000' ? 'rgba(255, 255, 255, 0.55)' : 'rgba(255, 255, 255, 0.14)';
  return { custom: true, style: { background, ...tokens(fg, glass) } };
}
